import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import feisLogo from "../Assets/feis-logo.svg";
import { dashboardPath, scrollIfSamePage, signedIn } from "./session";

// The public pages' floating glass bar. The links sit in a white pill with a
// green bubble that marks the current page, follows the pointer or keyboard
// focus, and springs back. Below 960px the links move into a drop-down menu.
// `organiser`: the log in / sign up buttons go to the organiser screens.
// On phones the bar shows Log in and Sign up moves into the menu (Devin
// 2026-10-16).

const SiteHeader = ({ active, organiser = false }) => {
  const tray = useRef(null);
  const menuBtn = useRef(null);
  const [bubble, setBubble] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [menuShown, setMenuShown] = useState(false);
  const member = signedIn();

  const links = [
    { key: "events", label: "Events", to: member ? "/" : "/#events" },
    { key: "results", label: "Results", to: member ? "/results" : "/#season" },
    { key: "organisers", label: "For organisers", to: "/organisers" },
    { key: "about", label: "About", to: "/about" },
  ];

  const moveTo = useCallback((key) => {
    const el = key && tray.current?.querySelector(`a[data-k="${key}"]`);
    setBubble(el ? { key, left: el.offsetLeft, width: el.offsetWidth } : null);
  }, []);
  const home = useCallback(() => moveTo(active), [moveTo, active]);

  useLayoutEffect(() => {
    home();
    window.addEventListener("resize", home);
    if (document.fonts?.ready) document.fonts.ready.then(home);
    return () => window.removeEventListener("resize", home);
  }, [home]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The menu fades in after it is mounted and unmounts after it fades out.
  useEffect(() => {
    if (menu) {
      const f = requestAnimationFrame(() => setMenuShown(true));
      return () => cancelAnimationFrame(f);
    }
    setMenuShown(false);
    return undefined;
  }, [menu]);
  const [menuMounted, setMenuMounted] = useState(false);
  useEffect(() => {
    if (menu) { setMenuMounted(true); return undefined; }
    const t = setTimeout(() => setMenuMounted(false), 250);
    return () => clearTimeout(t);
  }, [menu]);

  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { setMenu(false); menuBtn.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu]);

  const login = organiser ? "/login-org" : "/login";
  const signup = organiser ? "/signup-org" : "/signup";
  const dashboard = dashboardPath();

  const actions = (inMenu) => (member ? (
    <Link className="sbtn" to={dashboard}>My dashboard</Link>
  ) : (
    <>
      <Link className="login" to={login}>
        {organiser && !inMenu ? <><span className="wide">Organiser log in</span><span className="narrow">Log in</span></> : "Log in"}
      </Link>
      <Link className="sbtn signup" to={signup}>
        {organiser && !inMenu ? <><span className="wide">Organiser sign up</span><span className="narrow">Sign up</span></> : "Sign up"}
      </Link>
    </>
  ));

  return (
    <header className={`top${scrolled ? " scrolled" : ""}${menu ? " open" : ""}`}>
      <div className="wrap">
        <nav className="bar" aria-label="Main">
          <Link className="logo" to="/"><img src={feisLogo} alt="Feis.link" width="107" height="36" /></Link>
          <div className="links ready" ref={tray} onMouseLeave={home} onBlur={(e) => { if (!tray.current?.contains(e.relatedTarget)) home(); }}>
            <span
              className="ind"
              aria-hidden="true"
              style={bubble ? { opacity: 1, width: bubble.width, transform: `translateX(${bubble.left}px)` } : { opacity: 0 }}
            />
            {links.map((l) => (
              <Link
                key={l.key}
                data-k={l.key}
                to={l.to}
                className={`${l.key === active ? "on" : ""}${bubble?.key === l.key ? " lit" : ""}`.trim() || undefined}
                aria-current={l.key === active ? "page" : undefined}
                onClick={scrollIfSamePage(l.to)}
                onMouseEnter={() => moveTo(l.key)}
                onFocus={() => moveTo(l.key)}
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="right">
            {actions(false)}
            <button
              ref={menuBtn}
              className="menu-btn"
              type="button"
              aria-expanded={menu}
              aria-controls="site-menu"
              aria-label="Menu"
              onClick={() => setMenu((m) => !m)}
            >
              <i /><i />
            </button>
          </div>
        </nav>
        {menuMounted && (
          <div className={`mnav${menuShown ? " show" : ""}`} id="site-menu" onClick={(e) => { if (e.target.closest("a")) setMenu(false); }}>
            <div className="mlinks">
              {links.map((l) => (
                <Link key={l.key} to={l.to} onClick={scrollIfSamePage(l.to)} className={l.key === active ? "on" : undefined} aria-current={l.key === active ? "page" : undefined}>
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="macts">{actions(true)}</div>
          </div>
        )}
      </div>
    </header>
  );
};

export default SiteHeader;
